import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Crown, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

// Single department horizontal timeline renderer
function SingleDepartmentTimeline({ application, statusFlow }) {
  const { t } = useTranslation();
  const currentIndex = statusFlow.findIndex((s) => s.value === application.status);

  // Include final status in the flow for display
  const displayFlow = [...statusFlow];
  if (application.status === "approved" || application.status === "rejected") {
    displayFlow.push({
      value: application.status,
      label: application.status === "approved" ? t("status.approved") : t("status.rejected"),
      isFinal: true
    });
  }

  return (
    <div className="w-full overflow-x-auto pb-2">
      <div className="flex items-center min-w-max">
        {displayFlow.map((step, index) => {
          const isCompleted = step.isFinal ? true : index <= currentIndex;
          const isCurrent = step.value === application.status;
          const isLast = index === displayFlow.length - 1;
          const isFinalStatus = step.isFinal;

          return (
            <React.Fragment key={step.value}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isFinalStatus
                      ? application.status === "approved"
                        ? "bg-emerald-800 text-white"
                        : "bg-red-600 text-white"
                      : isCompleted
                      ? application.department === "survey"
                        ? "bg-blue-700 text-white"
                        : "bg-emerald-800 text-white"
                      : "bg-stone-200 text-slate-400"
                  }`}
                >
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : index + 1}
                </div>
                <p
                  className={`text-xs mt-2 text-center max-w-[80px] leading-tight ${
                    isFinalStatus
                      ? application.status === "approved"
                        ? "text-emerald-800 font-semibold"
                        : "text-red-600 font-semibold"
                      : isCurrent
                      ? application.department === "survey"
                        ? "text-blue-700 font-semibold"
                        : "text-emerald-800 font-semibold"
                      : isCompleted
                      ? "text-slate-700"
                      : "text-slate-400"
                  }`}
                >
                  {step.label}
                </p>
                {isCurrent && !isFinalStatus && (
                  <span className="text-[10px] text-emerald-600 mt-0.5">
                    {t("status.current")}
                  </span>
                )}
              </div>
              {!isLast && (
                <div
                  className={`h-0.5 w-8 mx-1 flex-shrink-0 ${
                    isCompleted
                      ? application.department === "survey"
                        ? "bg-blue-700"
                        : "bg-emerald-800"
                      : "bg-stone-200"
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// Both departments (sequential) horizontal timeline renderer
function BothDepartmentsTimeline({ application, bothStatusFlow, getCurrentPhase }) {
  const { t } = useTranslation();
  const currentPhase = getCurrentPhase();

  const revenueSteps = bothStatusFlow.filter((s) => s.phase === "revenue");
  const surveySteps = bothStatusFlow.filter((s) => s.phase === "survey");

  const revenueCurrentIndex = revenueSteps.findIndex((s) => s.value === application.status);
  const surveyCurrentIndex = surveySteps.findIndex((s) => s.value === application.status);

  const isRevenueComplete = currentPhase === "survey" || application.status === "approved" || application.status === "rejected";
  const isSurveyComplete = application.status === "approved" || application.status === "rejected";

  return (
    <div className="space-y-4">
      {/* Revenue Phase */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              isRevenueComplete
                ? "bg-emerald-600 text-white"
                : currentPhase === "revenue"
                ? "bg-emerald-800 text-white"
                : "bg-stone-300 text-stone-600"
            }`}
          >
            {isRevenueComplete ? "✓" : "1"}
          </div>
          <span className={`text-sm font-semibold ${currentPhase === "revenue" ? "text-emerald-800" : "text-emerald-700"}`}>
            {t("status.revenuePhase")}
          </span>
          {currentPhase === "revenue" && (
            <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-emerald-300 text-xs py-0">
              {t("status.currentStage")}
            </Badge>
          )}
        </div>
        <div className="w-full overflow-x-auto pb-2 pl-7">
          <div className="flex items-center min-w-max">
            {revenueSteps.map((step, index) => {
              const isCompleted = isRevenueComplete ? true : index <= revenueCurrentIndex;
              const isCurrent = step.value === application.status && currentPhase === "revenue";
              const isLast = index === revenueSteps.length - 1;

              return (
                <React.Fragment key={step.value}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        isCompleted ? "bg-emerald-600 text-white" : "bg-stone-200 text-slate-400"
                      }`}
                    >
                      {isCompleted ? <CheckCircle className="w-3 h-3" /> : index + 1}
                    </div>
                    <p
                      className={`text-[10px] mt-1 text-center max-w-[70px] leading-tight ${
                        isCurrent ? "text-emerald-800 font-semibold" : isCompleted ? "text-slate-600" : "text-slate-400"
                      }`}
                    >
                      {step.label}
                    </p>
                  </div>
                  {!isLast && (
                    <div className={`h-0.5 w-6 mx-0.5 flex-shrink-0 ${isCompleted ? "bg-emerald-600" : "bg-stone-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Transition Arrow */}
      <div className="flex items-center justify-center">
        <div className={`px-3 py-1 rounded-full text-xs ${isRevenueComplete ? "bg-purple-100 text-purple-800" : "bg-stone-100 text-stone-500"}`}>
          ↓ {t("department.bothDesc")}
        </div>
      </div>

      {/* Survey Phase */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div
            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
              isSurveyComplete
                ? "bg-blue-600 text-white"
                : currentPhase === "survey"
                ? "bg-blue-700 text-white"
                : "bg-stone-300 text-stone-600"
            }`}
          >
            {isSurveyComplete ? "✓" : "2"}
          </div>
          <span className={`text-sm font-semibold ${currentPhase === "survey" ? "text-blue-800" : isRevenueComplete ? "text-blue-700" : "text-stone-500"}`}>
            {t("status.surveyPhase")}
          </span>
          {currentPhase === "survey" && application.status !== "approved" && (
            <Badge className="ml-2 bg-blue-100 text-blue-800 border-blue-300 text-xs py-0">
              {t("status.currentStage")}
            </Badge>
          )}
        </div>
        <div className="w-full overflow-x-auto pb-2 pl-7">
          <div className="flex items-center min-w-max">
            {surveySteps.map((step, index) => {
              const isCompleted = isSurveyComplete ? true : currentPhase === "survey" && index <= surveyCurrentIndex;
              const isCurrent = step.value === application.status && currentPhase === "survey";
              const isLast = index === surveySteps.length - 1;

              return (
                <React.Fragment key={step.value}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        isCompleted
                          ? "bg-blue-600 text-white"
                          : currentPhase === "survey"
                          ? "bg-stone-200 text-slate-400"
                          : "bg-stone-100 text-stone-300"
                      }`}
                    >
                      {isCompleted ? <CheckCircle className="w-3 h-3" /> : index + 1}
                    </div>
                    <p
                      className={`text-[10px] mt-1 text-center max-w-[70px] leading-tight ${
                        isCurrent
                          ? "text-blue-800 font-semibold"
                          : isCompleted
                          ? "text-slate-600"
                          : currentPhase === "survey"
                          ? "text-slate-400"
                          : "text-slate-300"
                      }`}
                    >
                      {step.label}
                    </p>
                  </div>
                  {!isLast && (
                    <div
                      className={`h-0.5 w-6 mx-0.5 flex-shrink-0 ${
                        isCompleted ? "bg-blue-600" : currentPhase === "survey" ? "bg-stone-200" : "bg-stone-100"
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Final Status */}
      {(application.status === "approved" || application.status === "rejected") && (
        <div className="flex items-center gap-3 pt-3 border-t border-stone-200">
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center ${
              application.status === "approved" ? "bg-emerald-800 text-white" : "bg-red-600 text-white"
            }`}
          >
            <CheckCircle className="w-4 h-4" />
          </div>
          <div>
            <p className={`text-sm font-bold ${application.status === "approved" ? "text-emerald-800" : "text-red-600"}`}>
              {application.status === "approved" ? t("status.approved") : t("status.rejected")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Status update buttons component
function StatusUpdateButtons({
  application,
  user,
  statusFlow,
  bothStatusFlow,
  getCurrentPhase,
  canApprove,
  canApproveRevenuePhase,
  isAtFinalReviewStage,
  isGOKOfficial,
  onStatusUpdate,
  onApproveReject,
  onRevenueApproval,
  onOverrideClick,
  validStatusData,
}) {
  const { t } = useTranslation();

  if (
    user?.role !== "official" ||
    application.status === "approved" ||
    application.status === "rejected"
  ) {
    return null;
  }

  // Use RBAC-aware valid statuses if available
  const validStatuses = validStatusData?.valid_statuses || [];
  const isSuperAuthority = validStatusData?.is_super_authority ?? true; // Default to true for backwards compatibility
  const rbacCanApprove = validStatusData?.can_approve ?? canApprove;
  const rbacCanReject = validStatusData?.can_reject ?? false;
  const rbacCanRevenueApprove = validStatusData?.can_revenue_approve ?? canApproveRevenuePhase;

  // Helper to check if a status is allowed
  const isStatusAllowed = (statusValue) => {
    if (!validStatusData || validStatuses.length === 0) {
      // Fallback to old behavior if no RBAC data
      return true;
    }
    return validStatuses.includes(statusValue);
  };

  return (
    <div className="mt-4 pt-4 border-t border-stone-200">
      <p className="text-sm font-medium text-slate-900 mb-3">
        {t("status.updateStatus")}
      </p>
      
      {/* Show RBAC notice for non-super-authority users */}
      {validStatusData && !isSuperAuthority && (
        <p className="text-xs text-amber-600 mb-3 flex items-center gap-1">
          <Lock className="w-3 h-3" />
          {t("rbac.limitedStatusAccess", "You can only advance to the next workflow step")}
        </p>
      )}
      
      <div className="flex flex-wrap gap-2">
        {application.department === "both" ? (
          <>
            {getCurrentPhase() === "revenue" &&
              bothStatusFlow
                .filter((s) => s.phase === "revenue")
                .map((step) => {
                  const allowed = isStatusAllowed(step.value);
                  const isCurrent = application.status === step.value;
                  return (
                    <Button
                      key={step.value}
                      size="sm"
                      variant="outline"
                      onClick={() => allowed && onStatusUpdate(step.value)}
                      disabled={isCurrent || !allowed}
                      className={`text-xs ${!allowed && !isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`status-update-${step.value}`}
                      title={!allowed && !isCurrent ? t("rbac.statusNotAllowed", "Status not available at your level") : ""}
                    >
                      {step.label}
                      {!allowed && !isCurrent && <Lock className="w-3 h-3 ml-1 text-slate-400" />}
                    </Button>
                  );
                })}
            {getCurrentPhase() === "survey" &&
              bothStatusFlow
                .filter((s) => s.phase === "survey")
                .map((step) => {
                  const allowed = isStatusAllowed(step.value);
                  const isCurrent = application.status === step.value;
                  return (
                    <Button
                      key={step.value}
                      size="sm"
                      variant="outline"
                      onClick={() => allowed && onStatusUpdate(step.value)}
                      disabled={isCurrent || !allowed}
                      className={`text-xs border-blue-200 hover:bg-blue-50 ${!allowed && !isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`status-update-${step.value}`}
                      title={!allowed && !isCurrent ? t("rbac.statusNotAllowed", "Status not available at your level") : ""}
                    >
                      {step.label}
                      {!allowed && !isCurrent && <Lock className="w-3 h-3 ml-1 text-slate-400" />}
                    </Button>
                  );
                })}
          </>
        ) : (
          statusFlow.map((step) => {
            const allowed = isStatusAllowed(step.value);
            const isCurrent = application.status === step.value;
            return (
              <Button
                key={step.value}
                size="sm"
                variant="outline"
                onClick={() => allowed && onStatusUpdate(step.value)}
                disabled={isCurrent || !allowed}
                className={`text-xs ${!allowed && !isCurrent ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`status-update-${step.value}`}
                title={!allowed && !isCurrent ? t("rbac.statusNotAllowed", "Status not available at your level") : ""}
              >
                {step.label}
                {!allowed && !isCurrent && <Lock className="w-3 h-3 ml-1 text-slate-400" />}
              </Button>
            );
          })
        )}

        {rbacCanRevenueApprove && (
          <Button
            size="sm"
            onClick={onRevenueApproval}
            className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs"
            data-testid="revenue-approve-button"
          >
            {t("status.revenueApprove")} →
          </Button>
        )}

        {rbacCanApprove && isAtFinalReviewStage ? (
          <>
            <Button
              size="sm"
              onClick={() => onApproveReject("approved", "Application approved")}
              className="bg-emerald-800 hover:bg-emerald-900 text-white text-xs"
              data-testid="approve-button"
            >
              {t("status.approve")}
            </Button>
            {rbacCanReject && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onApproveReject("rejected", "Application rejected")}
                className="text-xs"
                data-testid="reject-button"
              >
                {t("status.reject")}
              </Button>
            )}
          </>
        ) : (
          !rbacCanRevenueApprove && isSuperAuthority && (
            <p className="text-xs text-slate-500 italic ml-2">
              {application.department === "both"
                ? getCurrentPhase() === "revenue"
                  ? t("status.onlyTahsildarCanApprove")
                  : t("status.onlyDDLRCanApprove")
                : application.department === "survey"
                ? t("status.onlyDDLRCanApprove")
                : t("status.onlyTahsildarCanApprove")}
            </p>
          )
        )}

        {isGOKOfficial && (
          <Button
            size="sm"
            variant="outline"
            onClick={onOverrideClick}
            className="ml-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="gok-override-button"
          >
            <Crown className="w-3 h-3 mr-1" />
            {t("gok.overrideDecision")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function StatusTimeline({
  application,
  user,
  statusFlow,
  bothStatusFlow,
  getCurrentPhase,
  canApprove,
  canApproveRevenuePhase,
  isAtFinalReviewStage,
  isGOKOfficial,
  onStatusUpdate,
  onApproveReject,
  onRevenueApproval,
  onOverrideClick,
  validStatusData,
}) {
  const { t } = useTranslation();

  return (
    <Card className="p-4 bg-white border border-stone-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-manrope font-bold text-emerald-700">
          {t("status.timeline")}
        </h2>
        <Badge
          variant="outline"
          className={
            application.department === "survey"
              ? "bg-blue-50 text-blue-800 border-blue-200"
              : application.department === "both"
              ? "bg-purple-50 text-purple-800 border-purple-200"
              : "bg-emerald-50 text-emerald-800 border-emerald-200"
          }
        >
          {t(`department.${application.department || "revenue"}`)}
        </Badge>
      </div>

      {application.department === "both" ? (
        <BothDepartmentsTimeline
          application={application}
          bothStatusFlow={bothStatusFlow}
          getCurrentPhase={getCurrentPhase}
        />
      ) : (
        <SingleDepartmentTimeline
          application={application}
          statusFlow={statusFlow}
        />
      )}

      <StatusUpdateButtons
        application={application}
        user={user}
        statusFlow={statusFlow}
        bothStatusFlow={bothStatusFlow}
        getCurrentPhase={getCurrentPhase}
        canApprove={canApprove}
        canApproveRevenuePhase={canApproveRevenuePhase}
        isAtFinalReviewStage={isAtFinalReviewStage}
        isGOKOfficial={isGOKOfficial}
        onStatusUpdate={onStatusUpdate}
        onApproveReject={onApproveReject}
        onRevenueApproval={onRevenueApproval}
        onOverrideClick={onOverrideClick}
        validStatusData={validStatusData}
      />
    </Card>
  );
}
